import {
  CheckIcon,
  FileUpload,
  CircularLoader,
  Card,
  CardHeader,
} from "@forge/common";
import React, { useState } from "react";
import { BodyText, NormalText } from "../../common/components/title-container/LText";
import { HeaderComponent, FooterComponent } from "../popup";
import "./iso-popup.scss";
import { useDispatch } from "react-redux";
import { ISO_FILE_MANAGEMENT_CONSTANTS } from "../utils/constant";
import { btnResponsive } from "../../common/utils/helper";
import { rendererFileSizeCol } from "../../file-management/utils/helper";
 
const hasIsoOrUdfMagic = async (file: File) => {
  const SECTOR_SIZE = 2048;
  const needIso = SECTOR_SIZE * 16 + 6;   // minimum file size (in bytes) to safely read "CD001" at sector 16 (~32 KB, 32,774 bytes)
  const avdpOffset = SECTOR_SIZE * 256;   // 0x80000
  const needAvdpTag = avdpOffset + 16;
 
  const readBytes = async (start: number, length: number) => {
    const buf = await file.slice(start, start + length).arrayBuffer();
    return new Uint8Array(buf);
  };
 
  const readAscii = async (start: number, length: number) => {
    const bytes = await readBytes(start, length);
    if (bytes.length < length) return "";
    return String.fromCharCode(...new Uint8Array(bytes) as unknown as number[]);
  };
 
  // 1) ISO9660 magic at sector 16, byte 1 ("CD001")
  if (file.size >= needIso) {
    const isoMagic = await readAscii(SECTOR_SIZE * 16 + 1, 5);
    if (isoMagic === "CD001") return true;
  } else {
    // too small to hold ISO PVD
    return false;
  }
 
  // 2) UDF AVDP check at sector 256
  if (file.size < needAvdpTag) return false;
  const tag = await readBytes(avdpOffset, 16);
  const tagIdentifier = tag[0] | (tag[1] << 8);
  if (tagIdentifier !== 2)
  {
    return false;
  }
 
  // Tag checksum (skip byte 4)
  let checksum = 0;
  for (let i = 0; i < 16; i++) if (i !== 4) checksum += tag[i];
  checksum &= 0xff;
  if (checksum !== tag[4]){
     console.warn("Tag checksum mismatch");
    return false;
  }
  // Check for Descriptor CRC/Length validation
  const descriptorCRC = tag[8] | (tag[9] << 8);
  const descriptorCRCLength = tag[10] | (tag[11] << 8);
  if (descriptorCRCLength === 0) return false;
  if (file.size < avdpOffset + 16 + descriptorCRCLength) return false;
 
  const descriptorBody = await readBytes(avdpOffset + 16, descriptorCRCLength);
  if (descriptorBody.length !== descriptorCRCLength){
    console.warn("Descriptor body length mismatch");
    return false;
  }
 // CRC-16-CCITT calculation
  const calcCRC16 = (data: Uint8Array) => {
    let crc = 0;
    for (let b of data as unknown as number[]) {
      crc ^= b << 8;
      for (let i = 0; i < 8; i++) {
        crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1);
      }
      crc &= 0xffff;
    }
    return crc;
  };
 
  if (calcCRC16(descriptorBody) !== descriptorCRC){
    console.warn("Descriptor CRC mismatch");
    return false;}
 
  return true; // only true if ISO CD001 or valid UDF AVDP passed
};
 
const UploadIso = ({
  title,
  description,
  selectedItems,
  btnText1,
  btnText,
  onCancel,
  onTrigger,
}: {
  title: string;
  description?: string;
  selectedItems?: any[];
  btnText1?: string;
  btnText?: string;
  onCancel: () => void;
  onTrigger: (files: File[]) => void;
}) => {
  const disptach = useDispatch()
  const [error,setError] = useState<string | null>(null)
  const [fileList, setFileList] = React.useState<File[]>([]);
 
  const onFileUpload = async (fileList: FileList) => {
    setError(null);
    const files = Array.from(fileList);
 
    // Check if the total file size exceeds 15 GB
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > 15 * 1024 * 1024 * 1024) {
      setError(ISO_FILE_MANAGEMENT_CONSTANTS.TOTAL_UPLOAD_SIZE_CANNOT_EXCEED)
      return;
    }
 
    const maxFileName = files.filter((file) => file.name?.length > 128);
    if (maxFileName.length > 0) {
      setError(ISO_FILE_MANAGEMENT_CONSTANTS.MAX_FILE_NAME)
      return;
    }
    // Check if all files are ISO format
    const nonIsoFiles = files.filter((file) => !file.name.toLowerCase().endsWith(".iso"));
    if (nonIsoFiles.length > 0) {
      setError(ISO_FILE_MANAGEMENT_CONSTANTS.FILES_PERMITTED);
      return;
    }
 
    // Magic check (ISO9660/UDF) to catch renamed non-ISO files
    const magicChecks = await Promise.all(
      files.map(async (f) => {
        try {
          return await hasIsoOrUdfMagic(f);
        } catch {
          return false;
        }
      })
    );
    if (magicChecks.some((ok) => !ok)) {
      setError(ISO_FILE_MANAGEMENT_CONSTANTS.FILES_PERMITTED);
      return;
    }

    setFileList((prev) => [...prev, ...files]);
  };

  return (
    <Card className="mx-auto w-full border-[0px] border-card-none bg-[#202020]">
      <div className="flexContainer flexDirection-column w-[400px] h-[500px] xl-high:w-[600px] xl-high:h-[600px]">
        <HeaderComponent title={title} onCancel={onCancel} />
        <div
          className="mt-5 border-2 border-dashed border-[#6E6E6E] rounded-md p-6 py-8 cursor-pointer"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer.files?.length > 0) {
              onFileUpload(e.dataTransfer.files);
            }
          }}
        >
          <FileUpload
            data-testid="file-input"
            accept={["text/json"]}
            containerClassName="custom-fileupload-no-border h-[65px]"
            i18nBrowse="Browse for files"
            i18nDragDrop="Drag and drop here"
            i18nOr="or"
            id="file-upload"
            onFileUpload={onFileUpload}
            orientation="horizontal"
            variant="drag-drop"
            disabled={fileList?.length !== 0}
            error={error!==null}
          />
        
        {error && <NormalText title={error} className="text-xs font-medium !text-[#FF644C] mt-1 text-center" />}
        <div className="mt-2">
           <NormalText
            className={`text-xs ${btnResponsive("text-xs")} xl-high:mt-3 font-medium text-[#D0D0D0] text-center`}
            title={"Only ISO files are permitted."}
          />
          <NormalText
            className={`text-xs ${btnResponsive("text-xs")} font-medium text-[#D0D0D0] mt-1 text-center`}
            title={"Total upload size cannot exceed 15 GB."}
          />
           <NormalText
            className={`text-xs ${btnResponsive("text-xs")} xl-high:mt-3 font-medium text-[#D0D0D0] mt-1 text-center`}
            title={"Please do not refresh or close the page while upload is in progress."}
          />
        </div>
        </div>
        <div className="overflow-auto h-[200px] xl-high:h-[400px]  mt-3">
          {fileList?.map((file: File, index: number) => {
            return (
              <div
                className="w-100 border-t-[1px] border-black cardListing"
                key={index}
              >
                <CardHeader
                  avatar={
                    <div className="flexContainer mt-[15px] h-100 alignItems-center justify-center gap-2">
                      <div className="gradient-border">
                        <div className="transparent-circle"></div>
                      </div>
                    </div>
                  }
                  slotRight={
                    <div className="flexContainer mt-[15px] h-100 alignItems-center justify-center gap-2">
                      <CheckIcon className="text-xl cursor-pointer text-[#5A8028] font-bold" />
                    </div>
                  }
                >
            <div className="w-60 overflow-hidden text-ellipsis mt-2">
                  <NormalText
                    title={file.name}
                    className={`text-xs ${btnResponsive("text-xs")} leading-[32px] xl-high:min-h-[30px] font-medium text-[#D0D0D0] truncate`}
                  />
                  </div>
                  <NormalText
                    title={rendererFileSizeCol(file.size?.toString())}
                    className={`text-xs ${btnResponsive("text-xs")} leading-[32px] font-medium text-[#A0A0A0] ml-2`}
                  />
                </CardHeader>
              </div>
            );
          })}
        </div>
        <div className="flexContainer justify-center items-end mt-4">
          <FooterComponent
            btnText1={btnText1}
            btnText={btnText}
            onCancel={onCancel}
            onTrigger={() => onTrigger(fileList)}
            disabled={fileList?.length === 0}
          />
        </div>
      </div>
    </Card>
  );
};

export default UploadIso;
